// import React, { useState } from "react";
import { ErrorMessage, FieldArray, FormikProps } from "formik";
import { AiOutlinePlus } from "react-icons/ai";
import { MdDelete } from "react-icons/md";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMCheckbox from "src/components/UI/atoms/formFields/ATMCheckbox/ATMCheckbox";
import ATMHTMLEditor from "src/components/UI/atoms/formFields/ATMHTMLEditor/ATMHTMLEditor";
import ATMMultiSelect from "src/components/UI/atoms/formFields/ATMMultiSelect/ATMMultiSelect";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import ATMTagAutoComplete from "src/components/UI/atoms/formFields/ATMTagAutoComplete/ATMTagAutoComplete";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import useQuestionCategory from "src/hooks/useQuestionCategory";
import useQuestionTechnology from "src/hooks/useQuestionTechnology";
import { TestQuestionFormValues } from "src/models/TestQuestion.model";
import { useGetAllQuestionTagQuery } from "src/services/QuestionCategoryServices";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";

type Props = {
  formikProps: FormikProps<TestQuestionFormValues>;
  editorValue?: string;
  formType: "Add" | "Edit";
  setSaveNextChecked?: any;
  saveNextChecked?: any;
};

const TestQuestionForm = ({
  formikProps,
  editorValue,
  formType,
  setSaveNextChecked,
  saveNextChecked,
}: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;

  //   Get All Countries
  const { questionCategory, isDataLoading: isQuestionCategoryLoading } =
    useQuestionCategory();
  const { questionTechnology, isDataLoading: isQuestionTechnologyLoading } =
    useQuestionTechnology();

  const { data: AllTagsData } = useGetAllQuestionTagQuery("");
  const tagsOptions = AllTagsData?.data?.map((el: any) => {
    return {
      title: el?.tagName,
    };
  });

  return (
    <div className="flex flex-col flex-1 h-full gap-4 p-4 overflow-hidden">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-medium text-slate-700">
          {formType === "Add" ? "Add Test Question" : "Update Test Question"}
        </h1>
      </div>
      <div className="flex flex-col w-full h-full gap-4 overflow-scroll">
        <div className="grid items-center w-full grid-cols-4 gap-4">
          {/* Title */}
          <ATMTextField
            required
            name="questionTitle"
            value={values?.questionTitle}
            onChange={(e) => setFieldValue("questionTitle", e.target.value)}
            label="Title "
            placeholder="Enter Title "
            onBlur={handleBlur}
          />
          {/* Category */}

          <ATMMultiSelect
            required
            name="questionCategoryId"
            value={values.questionCategoryId}
            onChange={(newValue) =>
              setFieldValue("questionCategoryId", newValue)
            }
            onBlur={handleBlur}
            label="Category"
            placeholder="Select Category"
            options={questionCategory?.map((el: any) => {
              return {
                label: el?.categoryName,
                value: el?._id,
              };
            })}
            isLoading={isQuestionCategoryLoading}
          />
          {/* Technology */}

          <ATMMultiSelect
            required
            name="questionTechnologyId"
            value={values.questionTechnologyId}
            onChange={(newValue) =>
              setFieldValue("questionTechnologyId", newValue)
            }
            onBlur={handleBlur}
            label="Technology"
            placeholder="Select Technology"
            options={questionTechnology?.map((el: any) => {
              return {
                label: el?.questionTechnology,
                value: el?._id,
              };
            })}
            isLoading={isQuestionTechnologyLoading}
          />
          {/* questionTag */}

          <ATMTagAutoComplete
            placeholder="Enter tag"
            name="questionTag"
            value={values?.questionTag}
            onChange={(value: any) => {
              setFieldValue("questionTag", value);
            }}
            label="Tags"
            required
            option={tagsOptions || []}
          />
        </div>

        {/* description */}
        <div className="z-0">
          <ATMHTMLEditor
            required
            name="questionDescription"
            key={editorValue}
            label="Description"
            value={values?.questionDescription}
            placeholder="Write here...."
            onChange={(newValue: any) => {
              setFieldValue("questionDescription", newValue);
            }}
          />
        </div>
        <div className="flex items-end gap-4 z-40">
          {/* type */}
          <ATMSelect
            required
            name="questionType"
            value={values?.questionType}
            onChange={(value) => {
              setFieldValue("questionType", value);
              setFieldValue(`questionOptions`, [
                {
                  optionName: "",
                  isCorrect: false,
                },
                {
                  optionName: "",
                  isCorrect: false,
                },
              ]);
            }}
            options={[
              {
                label: "MCQ",
                value: "MCQ",
              },
              {
                label: "MACHINE TEST",
                value: "MACHINE_TEST",
              },
            ]}
            label="Type"
          />
          {/* Problem Answer Type */}
          <div className="p-2 rounded border-divider bg-gray-50">
            {values?.questionType?.value === "MCQ" && (
              <ATMCheckbox
                label="Multiple Options"
                checked={values?.problemAnswerType}
                onChange={(checked) => {
                  setFieldValue("problemAnswerType", checked);
                  const filterKey = values?.questionOptions?.map((el: any) => {
                    return {
                      optionName: el?.optionName,
                      isCorrect: !values?.problemAnswerType
                        ? el?.isCorrect
                        : false,
                    };
                  });
                  setFieldValue("questionOptions", filterKey);
                }}
              />
            )}
          </div>
        </div>
        {values?.questionType?.value === "MCQ" && (
          <div className="flex items-center gap-2">
            <div className="font-semibold text-md text-primary-dark">Options</div>
            <ErrorMessage name={`questionOptions[${0}].isCorrect`}>
              {(errMsg) => {
                return (
                  <p className="font-poppins text-[14px] text-start mt-0 text-red-500">
                    {errMsg}
                  </p>
                )
              }}
            </ErrorMessage>
          </div>
        )}
        {values?.questionType?.value === "MCQ" && (
          <div className="p-4 border rounded-xl border-divider drop-shadow-sm bg-gray-50">
            <div className="flex flex-col gap-4">
              <FieldArray
                name="questionOptions"
                render={({ push, remove }) => (
                  <>
                    {values?.questionOptions?.map(
                      (option: any, index: number) => {
                        const isChecked = option?.isCorrect;
                        return (
                          <div key={index}>
                            <div className="absolute right-0 z-30 flex justify-end mx-4 cursor-pointer">
                              <MdDelete
                                color="#F70913"
                                size={30}
                                className="pl-1 cursor-pointer"
                                onClick={() => {
                                  showConfirmationDialog({
                                    title: "Heads Up",
                                    text: "Are you sure want to Delete this Option?",
                                    icon: "question",
                                    showCancelButton: true,
                                    next: (result) => {
                                      if (result.isConfirmed) {
                                        remove(index);
                                      }
                                    },
                                  });
                                }}
                              />
                            </div>
                            <div className="relative">
                              <div className="absolute z-20 left-20">
                                <ATMCheckbox
                                  label="Correct Answer"
                                  checked={isChecked}
                                  onChange={(checked) => {
                                    setFieldValue(
                                      `questionOptions[${index}].isCorrect`,
                                      checked
                                    );

                                    // Logic for SINGLE or MULTIPLE choice handling
                                    if (!values?.problemAnswerType) {
                                      // For SINGLE choice, uncheck all other options
                                      values?.questionOptions?.forEach(
                                        (opt: any, idx: number) => {
                                          if (idx !== index) {
                                            setFieldValue(
                                              `questionOptions[${idx}].isCorrect`,
                                              false
                                            );
                                          }
                                        }
                                      );
                                    }
                                  }}
                                />
                              </div>
                              <ATMHTMLEditor
                                required
                                name={`questionOptions[${index}].optionName`}
                                key={index}
                                label="Option"
                                value={option.optionName}
                                placeholder="Write here...."
                                onChange={(newValue: any) => {
                                  setFieldValue(
                                    `questionOptions[${index}].optionName`,
                                    newValue
                                  );
                                }}
                              />
                            </div>
                          </div>
                        );
                      }
                    )}
                    <div className="flex mt-1">
                      <p
                        className="text-link font-semibold text-base text-[16px] max-[1024px]:text-sm flex items-center cursor-pointer"
                        onClick={() =>
                          push({
                            optionName: "",
                            isCorrect: false,
                          })
                        }
                      >
                        <AiOutlinePlus
                          size={18}
                          color="#0064C4"
                          className="mr-2"
                        />
                        Add Option
                      </p>
                    </div>
                  </>
                )}
              />
            </div>
          </div>
        )}

        <ATMHTMLEditor
          name="answer"
          key={editorValue}
          label="Answer"
          value={values?.answer}
          placeholder="Write here...."
          onChange={(newValue: any) => {
            setFieldValue(`answer`, newValue);
          }}
        />
      </div>

      <div className="flex items-center justify-between w-full pt-4 border-t h-fit">
        {formType === "Add" && (
          <ATMCheckbox
            label="Save and Next"
            checked={saveNextChecked}
            onChange={(checked) => {
              setSaveNextChecked(checked);
            }}
          />
        )}
        <ATMLoadingButton
          isLoading={isSubmitting}
          type="submit"
          className="w-32"
        >
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default TestQuestionForm;
